/**
 * A session, cut into the stretches that each prompt set off.
 *
 * A session is one long strip of events, and the question people actually ask of it is smaller:
 * "what happened after I asked for that?" A prompt is on the same strip — the hook reports it —
 * so the strip can be cut at each one, and everything until the next prompt is that prompt's
 * stretch.
 *
 * Every figure here is summed from what the agent already reported, event by event. Nothing is
 * apportioned, averaged or guessed: a stretch with no test run says nothing about tests rather
 * than dividing the session's total by the number of prompts, which would be a number that looks
 * like a measurement and is not one.
 */

import type { TimelineEntry } from './types'

export interface PromptMetrics {
  seconds: number
  filesChanged: number
  linesAdded: number
  linesDeleted: number
  /** Paths the agent reported touching in this stretch, without repeats. */
  files: string[]
  testsPassed: number
  testsTotal: number
  testRuns: number
  tokens: number
  /** Null when no event in the stretch carried a cost: no cost is not zero cost. */
  costUsd: number | null
  stepsCompleted: number
}

export interface PromptSegment {
  /** The prompt event's id, or "start" for whatever happened before the first prompt. */
  id: string
  at: string
  /** When the next prompt arrived; null while this is the last stretch. */
  until: string | null
  /** How long the prompt was, when the hook said. Null for the opening stretch. */
  chars: number | null
  events: TimelineEntry[]
  metrics: PromptMetrics
}

const PROMPT = 'prompt.submitted'

export function promptSegments(timeline: TimelineEntry[]): PromptSegment[] {
  const ordered = [...timeline].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
  if (ordered.length === 0) return []

  const segments: PromptSegment[] = []
  let current: { id: string; at: string; chars: number | null; events: TimelineEntry[] } | null = null

  const open = (id: string, at: string, chars: number | null): void => {
    current = { id, at, chars, events: [] }
  }

  const close = (until: string | null): void => {
    if (!current) return
    segments.push({
      id: current.id,
      at: current.at,
      until,
      chars: current.chars,
      events: current.events,
      metrics: measure(current.events, current.at, until),
    })
    current = null
  }

  for (const entry of ordered) {
    if (entry.eventType === PROMPT) {
      close(entry.occurredAt)
      open(entry.eventId || entry.id, entry.occurredAt, number(entry.payload?.chars))
      continue
    }

    // Whatever happened before the first prompt is its own stretch: an agent often opens a session
    // and plans before anybody types anything, and that work is not the first prompt's.
    if (!current) open('start', entry.occurredAt, null)
    current!.events.push(entry)
  }

  close(null)

  // Newest first, as the log reads.
  return segments.reverse()
}

function measure(events: TimelineEntry[], at: string, until: string | null): PromptMetrics {
  const files = new Set<string>()
  let filesChanged = 0
  let linesAdded = 0
  let linesDeleted = 0
  let testsPassed = 0
  let testsTotal = 0
  let testRuns = 0
  let tokens = 0
  let cost: number | null = null
  let stepsCompleted = 0

  for (const entry of events) {
    const payload = entry.payload ?? {}

    switch (entry.eventType) {
      case 'code.changed':
        filesChanged += number(payload.filesChanged) ?? 0
        linesAdded += number(payload.linesAdded) ?? 0
        linesDeleted += number(payload.linesDeleted) ?? 0
        for (const path of Array.isArray(payload.files) ? payload.files : []) {
          if (typeof path === 'string') files.add(path)
        }
        break

      case 'test.completed':
        testRuns += 1
        testsPassed += number(payload.passed) ?? 0
        testsTotal += number(payload.total) ?? 0
        break

      case 'ai.usage':
        tokens += (number(payload.inputTokens) ?? 0) + (number(payload.outputTokens) ?? 0)
        if (number(payload.costUsd) != null) cost = (cost ?? 0) + number(payload.costUsd)!
        break

      case 'step.completed':
        stepsCompleted += 1
        break

      default:
        break
    }
  }

  const last = events.at(-1)?.occurredAt ?? at
  const end = until ?? last
  const seconds = Math.max(0, Math.round((Date.parse(end) - Date.parse(at)) / 1000))

  return { seconds, filesChanged, linesAdded, linesDeleted, files: [...files], testsPassed, testsTotal, testRuns, tokens, costUsd: cost, stepsCompleted }
}

function number(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}
